import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import {
  ChevronRight,
  ChevronLeft,
  School,
  Layers,
  Users,
  Plus,
  Trash2,
  Loader2,
  Check,
  Mail,
  X,
  ChevronDown,
  ChevronUp,
  Clock,
  AlertTriangle,
  CheckCircle2,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import TeacherImportDialog from '@/components/TeacherImportDialog';
import { ROLE_LABELS, type AppRole } from '@/lib/roles';
import { canCreateClass, getPlanLimits, planLimitMessage } from '@/lib/entitlements';

// ---- Wizard types (first-time setup) -------------------------------------

interface ClassInput {
  id: string;
  name: string;
}

interface YearGroupInput {
  id: string;
  name: string;
  classes: ClassInput[];
}

interface KeyStageInput {
  id: string;
  name: string;
  yearGroups: YearGroupInput[];
}

const STEPS = [
  { id: 1, title: 'School Details', icon: School },
  { id: 2, title: 'Structure', icon: Layers },
  { id: 3, title: 'Complete', icon: Check },
];

// ---- Management types (edit mode) ----------------------------------------

interface ClassRow {
  id: string;
  name: string;
  is_custom: boolean;
  year_group_id: string | null;
  year_groups: { id: string; name: string } | null;
}

interface TeacherRow {
  user_id: string;
  full_name: string | null;
  email: string | null;
  role: AppRole;
  class_ids: string[];
  is_confirmed: boolean;
}

interface PendingInvitationRow {
  id: string;
  email: string;
  invited_at: string;
}

// ---- Component -----------------------------------------------------------

export default function SchoolSetup() {
  const { user, profile, refreshProfile, hasRole, schoolPlan, planTier } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const hasSchool = !!profile?.school_id;
  // Set once the wizard creates the school, so the "Setup Complete!" step still renders after
  // refreshProfile() flips hasSchool to true (which would otherwise switch to management mode).
  const [setupJustCompleted, setSetupJustCompleted] = useState(false);
  const isAdmin = hasRole('school_admin');

  // Users without a school are bootstrapping one (school_admin is granted by the school-setup
  // edge function), so let them in. Once a school exists, only school-plan admins may manage it.
  useEffect(() => {
    if (user && hasSchool && (!isAdmin || schoolPlan !== 'school')) {
      navigate('/dashboard');
    }
  }, [user, hasSchool, isAdmin, schoolPlan, navigate]);

  // ---- WIZARD state -------------------------------------------------------

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [schoolName, setSchoolName] = useState('');
  const [dfeNumber, setDfeNumber] = useState('');
  const [postcode, setPostcode] = useState('');
  const [keyStages, setKeyStages] = useState<KeyStageInput[]>([
    {
      id: crypto.randomUUID(),
      name: 'KS1',
      yearGroups: [
        { id: crypto.randomUUID(), name: 'Year 1', classes: [{ id: crypto.randomUUID(), name: 'Class 1A' }] },
        { id: crypto.randomUUID(), name: 'Year 2', classes: [{ id: crypto.randomUUID(), name: 'Class 2A' }] },
      ],
    },
    {
      id: crypto.randomUUID(),
      name: 'KS2',
      yearGroups: [
        { id: crypto.randomUUID(), name: 'Year 3', classes: [{ id: crypto.randomUUID(), name: 'Class 3A' }] },
        { id: crypto.randomUUID(), name: 'Year 4', classes: [{ id: crypto.randomUUID(), name: 'Class 4A' }] },
        { id: crypto.randomUUID(), name: 'Year 5', classes: [{ id: crypto.randomUUID(), name: 'Class 5A' }] },
        { id: crypto.randomUUID(), name: 'Year 6', classes: [{ id: crypto.randomUUID(), name: 'Class 6A' }] },
      ],
    },
  ]);

  // ---- MANAGEMENT state ---------------------------------------------------

  // School details edit
  const [editName, setEditName] = useState('');
  const [editDfe, setEditDfe] = useState('');
  const [editPostcode, setEditPostcode] = useState('');
  const [savingDetails, setSavingDetails] = useState(false);

  // Classes
  const [classes, setClasses] = useState<ClassRow[]>([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [newClassName, setNewClassName] = useState('');
  const [newClassYearGroupId, setNewClassYearGroupId] = useState('');
  const [newClassCustom, setNewClassCustom] = useState(false);
  const [addingClass, setAddingClass] = useState(false);
  const [yearGroups, setYearGroups] = useState<{ id: string; name: string }[]>([]);
  const [editingClassId, setEditingClassId] = useState<string | null>(null);
  const [editingClassName, setEditingClassName] = useState('');

  // Teachers
  const [teachers, setTeachers] = useState<TeacherRow[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitationRow[]>([]);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFullName, setInviteFullName] = useState('');
  const [inviting, setInviting] = useState(false);
  const [teacherImportOpen, setTeacherImportOpen] = useState(false);
  const [expandedTeacherId, setExpandedTeacherId] = useState<string | null>(null);
  const [savingAssignment, setSavingAssignment] = useState<string | null>(null);
  const [revokingInvitationId, setRevokingInvitationId] = useState<string | null>(null);
  const [roleUpdatingUserId, setRoleUpdatingUserId] = useState<string | null>(null);
  const [passwordResetUserId, setPasswordResetUserId] = useState<string | null>(null);
  const [resendingInviteId, setResendingInviteId] = useState<string | null>(null);
  const [emailDialogUser, setEmailDialogUser] = useState<TeacherRow | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [updatingEmailUserId, setUpdatingEmailUserId] = useState<string | null>(null);

  // ---- Load existing school data ------------------------------------------

  const loadSchoolData = useCallback(async () => {
    if (!profile?.school_id) return;

    // Fetch school details
    const { data: school } = await supabase
      .from('schools')
      .select('name, dfe_number, postcode')
      .eq('id', profile.school_id)
      .single();

    if (school) {
      setEditName(school.name ?? '');
      setEditDfe(school.dfe_number ?? '');
      setEditPostcode(school.postcode ?? '');
    }
  }, [profile?.school_id]);

  const loadClasses = useCallback(async () => {
    if (!profile?.school_id) return;
    setLoadingClasses(true);

    // Fetch year groups for this school
    const { data: ygs } = await supabase
      .from('year_groups')
      .select('id, name, key_stages!inner(school_id)')
      .eq('key_stages.school_id', profile.school_id)
      .order('name');

    setYearGroups((ygs ?? []).map((y) => ({ id: y.id, name: y.name })));
    if (ygs && ygs.length > 0 && !newClassYearGroupId) {
      setNewClassYearGroupId(ygs[0].id);
    }

    // Fetch standard classes (with year group)
    const { data: standardClasses } = await supabase
      .from('classes')
      .select('id, name, is_custom, year_group_id, year_groups(id, name)')
      .eq('is_custom', false)
      .not('year_group_id', 'is', null)
      .in('year_group_id', (ygs ?? []).map((y) => y.id))
      .order('name');

    // Fetch custom classes (no year group)
    const { data: customClasses } = await supabase
      .from('classes')
      .select('id, name, is_custom, year_group_id, year_groups(id, name)')
      .eq('is_custom', true)
      .order('name');

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const allClasses = [...(standardClasses ?? []), ...(customClasses ?? [])] as any[] as ClassRow[];
    setClasses(allClasses);
    setLoadingClasses(false);
  }, [profile?.school_id, newClassYearGroupId]);

  const loadTeachers = useCallback(async () => {
    if (!profile?.school_id) return;
    setLoadingTeachers(true);

    const [profilesRes, rolesRes, assignmentsRes, statusRes, invitationsRes] = await Promise.all([
      supabase
        .from('profiles')
        .select('user_id, full_name, email')
        .eq('school_id', profile.school_id)
        .neq('user_id', user?.id ?? ''),
      supabase
        .from('user_roles')
        .select('user_id, role'),
      supabase
        .from('teacher_classes')
        .select('user_id, class_id'),
      supabase.rpc('get_school_teacher_statuses', { p_school_id: profile.school_id }),
      supabase
        .from('school_invitations')
        .select('id, email, invited_at')
        .eq('school_id', profile.school_id)
        .eq('status', 'pending'),
    ]);

    if (!profilesRes.error && profilesRes.data) {
      // Build role map (user_id → first role)
      const roleMap = new Map<string, AppRole>();
      const rolePriority: AppRole[] = ['school_admin', 'slt', 'teacher', 'department_leader', 'head_teacher', 'parent'];
      for (const r of rolesRes.data ?? []) {
        const nextRole = r.role as AppRole;
        const currentRole = roleMap.get(r.user_id);
        if (!currentRole || rolePriority.indexOf(nextRole) < rolePriority.indexOf(currentRole)) {
          roleMap.set(r.user_id, nextRole);
        }
      }

      // Build class assignment map
      const assignmentMap = new Map<string, string[]>();
      for (const a of assignmentsRes.data ?? []) {
        const existing = assignmentMap.get(a.user_id) ?? [];
        existing.push(a.class_id);
        assignmentMap.set(a.user_id, existing);
      }

      // Build confirmation status map
      const confirmedMap = new Map<string, boolean>();
      for (const s of statusRes.data ?? []) {
        confirmedMap.set(s.user_id, s.is_confirmed);
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rows: TeacherRow[] = (profilesRes.data as any[]).map((p) => ({
        user_id: p.user_id,
        full_name: p.full_name,
        email: p.email,
        role: roleMap.get(p.user_id) ?? 'teacher',
        class_ids: assignmentMap.get(p.user_id) ?? [],
        is_confirmed: confirmedMap.get(p.user_id) ?? false,
      }));
      setTeachers(rows);
    }

    if (!invitationsRes.error && invitationsRes.data) {
      setPendingInvitations(invitationsRes.data as PendingInvitationRow[]);
    }

    setLoadingTeachers(false);
  }, [profile?.school_id, user?.id]);

  useEffect(() => {
    if (hasSchool) {
      loadSchoolData();
      loadClasses();
      loadTeachers();
    }
  }, [hasSchool, loadSchoolData, loadClasses, loadTeachers]);

  // ---- Management: school details -----------------------------------------

  const handleSaveDetails = async () => {
    if (!profile?.school_id || !editName.trim()) return;
    setSavingDetails(true);
    const { error } = await supabase
      .from('schools')
      .update({
        name: editName.trim(),
        dfe_number: editDfe.trim() || null,
        postcode: editPostcode.trim() || null,
      })
      .eq('id', profile.school_id);
    setSavingDetails(false);
    if (error) {
      toast({ title: 'Failed to update school', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'School details updated.' });
    }
  };

  // ---- Management: classes ------------------------------------------------

  const handleAddClass = async () => {
    if (!newClassName.trim()) return;

    // Enforce plan class limit before inserting.
    const limits = getPlanLimits(planTier);
    if (limits.maxClasses !== -1 && !canCreateClass(planTier, classes.length)) {
      toast({
        title: 'Class limit reached',
        description: planLimitMessage('class', limits.maxClasses, planTier),
        variant: 'destructive',
      });
      return;
    }

    setAddingClass(true);
    const { error } = await supabase.from('classes').insert({
      name: newClassName.trim(),
      year_group_id: newClassCustom ? null : (newClassYearGroupId || null),
      is_custom: newClassCustom,
    });
    setAddingClass(false);
    if (error) {
      toast({ title: 'Failed to add class', description: error.message, variant: 'destructive' });
    } else {
      setNewClassName('');
      setNewClassCustom(false);
      await loadClasses();
      toast({ title: 'Class added.' });
    }
  };

  const handleDeleteClass = async (classId: string) => {
    if (!window.confirm('Delete this class? This cannot be undone.')) return;
    const { error } = await supabase.from('classes').delete().eq('id', classId);
    if (error) {
      toast({ title: 'Failed to delete class', description: error.message, variant: 'destructive' });
    } else {
      await loadClasses();
      toast({ title: 'Class deleted.' });
    }
  };

  const handleSaveClassName = async (classId: string) => {
    if (!editingClassName.trim()) return;
    const { error } = await supabase
      .from('classes')
      .update({ name: editingClassName.trim() })
      .eq('id', classId);
    if (error) {
      toast({ title: 'Failed to rename class', description: error.message, variant: 'destructive' });
    } else {
      setEditingClassId(null);
      await loadClasses();
    }
  };

  // ---- Management: teachers -----------------------------------------------

  const handleInviteTeacher = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    const emailUsed = inviteEmail.trim();
    const { data, error } = await supabase.functions.invoke('invite-teacher', {
      body: { email: emailUsed, fullName: inviteFullName.trim() || undefined },
    });
    setInviting(false);
    if (error || data?.error) {
      toast({
        title: 'Invite failed',
        description: error?.message ?? data?.error ?? 'Unknown error',
        variant: 'destructive',
      });
      return;
    }
    setInviteEmail('');
    setInviteFullName('');
    await loadTeachers();
    const outcome = data?.outcome as string | undefined;
    if (outcome === 'already_linked') {
      toast({ title: 'Already in school.', description: `${emailUsed} is already an active teacher in this school.` });
    } else if (outcome === 'already_invited') {
      toast({ title: 'Already invited.', description: `${emailUsed} already has a pending invitation. They can accept it when they next log in.` });
    } else if (outcome === 'existing_user_notified') {
      toast({ title: 'Invitation created.', description: `${emailUsed} already has an account. They will see the invitation when they next log in.` });
    } else if (outcome === 'has_existing_school') {
      toast({ title: 'Cannot invite.', description: `${emailUsed} already belongs to another school. They would need to leave that school before joining this one.`, variant: 'destructive' });
    } else {
      toast({ title: 'Invite sent.', description: `${emailUsed} will receive an email invitation.` });
    }
  };

  const handleRemoveTeacher = async (teacherUserId: string) => {
    if (!window.confirm('Remove this teacher from your school?')) return;

    const target = teachers.find((teacher) => teacher.user_id === teacherUserId);
    const removableRoles: AppRole[] = ['teacher', 'slt', 'department_leader', 'head_teacher'];
    if (!target || !removableRoles.includes(target.role)) {
      toast({ title: 'Cannot remove this user.', variant: 'destructive' });
      return;
    }

    // 1. Remove all class assignments for this teacher
    const { error: tcError } = await supabase
      .from('teacher_classes')
      .delete()
      .eq('user_id', teacherUserId);
    if (tcError) {
      toast({ title: 'Failed to remove teacher', description: tcError.message, variant: 'destructive' });
      return;
    }

    // 2. Remove teacher role
    const { error: roleError } = await supabase
      .from('user_roles')
      .delete()
      .eq('user_id', teacherUserId)
      .eq('role', target.role);
    if (roleError) {
      toast({ title: 'Failed to remove teacher', description: roleError.message, variant: 'destructive' });
      return;
    }

    // 3. Clear school_id from their profile
    const { error: profileError } = await supabase
      .from('profiles')
      .update({ school_id: null })
      .eq('user_id', teacherUserId);
    if (profileError) {
      toast({ title: 'Failed to update teacher profile', description: profileError.message, variant: 'destructive' });
      return;
    }

    await loadTeachers();
    toast({ title: 'User removed from school.' });
  };

  const handleRoleChange = async (teacherUserId: string, nextRole: 'teacher' | 'slt') => {
    setRoleUpdatingUserId(teacherUserId);
    const { data, error } = await supabase.functions.invoke('school-user-admin', {
      body: { action: 'set_role', targetUserId: teacherUserId, role: nextRole },
    });
    setRoleUpdatingUserId(null);
    if (error || data?.error) {
      toast({
        title: 'Failed to update role',
        description: error?.message ?? data?.error ?? 'Unknown error',
        variant: 'destructive',
      });
      return;
    }
    await loadTeachers();
    toast({ title: 'Role updated.' });
  };

  const handleSendPasswordReset = async (teacher: TeacherRow) => {
    setPasswordResetUserId(teacher.user_id);
    const { data, error } = await supabase.functions.invoke('school-user-admin', {
      body: { action: 'send_password_reset', targetUserId: teacher.user_id },
    });
    setPasswordResetUserId(null);
    if (error || data?.error) {
      toast({
        title: 'Failed to send reset email',
        description: error?.message ?? data?.error ?? 'Unknown error',
        variant: 'destructive',
      });
      return;
    }
    toast({ title: 'Password reset email sent.', description: `A reset email was sent to ${teacher.email ?? 'the user'}.` });
  };

  const handleResendInvite = async (invitationId: string, email: string) => {
    setResendingInviteId(invitationId);
    const { data, error } = await supabase.functions.invoke('school-user-admin', {
      body: { action: 'resend_invite', invitationId },
    });
    setResendingInviteId(null);
    if (error || data?.error) {
      toast({
        title: 'Failed to resend invite',
        description: error?.message ?? data?.error ?? 'Unknown error',
        variant: 'destructive',
      });
      return;
    }
    if (data?.delivery === 'account_exists') {
      toast({
        title: 'Invite status refreshed.',
        description: `${email} already has an account. Ask them to sign in to see the pending invitation.`,
      });
      return;
    }
    toast({ title: 'Invite resent.', description: `A fresh invite email was sent to ${email}.` });
  };

  const openUpdateEmailDialog = (teacher: TeacherRow) => {
    setEmailDialogUser(teacher);
    setNewEmail(teacher.email ?? '');
  };

  const handleUpdateEmail = async () => {
    if (!emailDialogUser) return;
    setUpdatingEmailUserId(emailDialogUser.user_id);
    const { data, error } = await supabase.functions.invoke('school-user-admin', {
      body: {
        action: 'update_email',
        targetUserId: emailDialogUser.user_id,
        newEmail,
      },
    });
    setUpdatingEmailUserId(null);
    if (error || data?.error) {
      toast({
        title: 'Failed to update email',
        description: error?.message ?? data?.error ?? 'Unknown error',
        variant: 'destructive',
      });
      return;
    }
    await loadTeachers();
    toast({
      title: 'Email updated.',
      description: 'The existing account was preserved and the new email now applies immediately. No password was shown or changed here.',
    });
    setEmailDialogUser(null);
    setNewEmail('');
  };

  const handleToggleClassAssignment = async (teacherUserId: string, classId: string, assign: boolean) => {
    const key = `${teacherUserId}:${classId}`;
    setSavingAssignment(key);
    try {
      if (assign) {
        const { error } = await supabase
          .from('teacher_classes')
          .insert({ user_id: teacherUserId, class_id: classId });
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('teacher_classes')
          .delete()
          .eq('user_id', teacherUserId)
          .eq('class_id', classId);
        if (error) throw error;
      }
      await loadTeachers();
    } catch (err) {
      toast({ title: 'Failed to update class assignment', description: (err as Error).message, variant: 'destructive' });
    } finally {
      setSavingAssignment(null);
    }
  };

  const handleRevokeInvitation = async (invitationId: string, email: string) => {
    if (!window.confirm(`Revoke the invitation for ${email}?`)) return;
    setRevokingInvitationId(invitationId);
    const { error } = await supabase
      .from('school_invitations')
      .update({ status: 'revoked' })
      .eq('id', invitationId);
    setRevokingInvitationId(null);
    if (error) {
      toast({ title: 'Failed to revoke invitation', description: error.message, variant: 'destructive' });
    } else {
      await loadTeachers();
      toast({ title: 'Invitation revoked.' });
    }
  };

  // ---- Wizard helpers -----------------------------------------------------

  const progress = (currentStep / STEPS.length) * 100;

  const addYearGroup = (ksId: string) => setKeyStages((prev) => prev.map((ks) =>
    ks.id === ksId ? { ...ks, yearGroups: [...ks.yearGroups, { id: crypto.randomUUID(), name: `Year ${ks.yearGroups.length + 1}`, classes: [{ id: crypto.randomUUID(), name: 'Class A' }] }] } : ks
  ));

  const removeYearGroup = (ksId: string, ygId: string) => setKeyStages((prev) => prev.map((ks) =>
    ks.id === ksId ? { ...ks, yearGroups: ks.yearGroups.filter((yg) => yg.id !== ygId) } : ks
  ));

  const updateYearGroupName = (ksId: string, ygId: string, name: string) => setKeyStages((prev) => prev.map((ks) =>
    ks.id === ksId ? { ...ks, yearGroups: ks.yearGroups.map((yg) => yg.id === ygId ? { ...yg, name } : yg) } : ks
  ));

  const addClass = (ksId: string, ygId: string) => setKeyStages((prev) => prev.map((ks) =>
    ks.id === ksId ? { ...ks, yearGroups: ks.yearGroups.map((yg) =>
      yg.id === ygId ? { ...yg, classes: [...yg.classes, { id: crypto.randomUUID(), name: `Class ${String.fromCharCode(65 + yg.classes.length)}` }] } : yg
    ) } : ks
  ));

  const removeClass = (ksId: string, ygId: string, clsId: string) => setKeyStages((prev) => prev.map((ks) =>
    ks.id === ksId ? { ...ks, yearGroups: ks.yearGroups.map((yg) =>
      yg.id === ygId ? { ...yg, classes: yg.classes.filter((c) => c.id !== clsId) } : yg
    ) } : ks
  ));

  const updateClassName = (ksId: string, ygId: string, clsId: string, name: string) => setKeyStages((prev) => prev.map((ks) =>
    ks.id === ksId ? { ...ks, yearGroups: ks.yearGroups.map((yg) =>
      yg.id === ygId ? { ...yg, classes: yg.classes.map((c) => c.id === clsId ? { ...c, name } : c) } : yg
    ) } : ks
  ));

  const handleNext = () => {
    if (currentStep === 1 && !schoolName.trim()) {
      toast({ title: 'School name required', variant: 'destructive' });
      return;
    }
    setCurrentStep((prev) => Math.min(prev + 1, STEPS.length));
  };

  const handleBack = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

  const handleWizardSubmit = async () => {
    if (!user) return;
    setIsSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('school-setup', {
        body: { schoolName, dfeNumber: dfeNumber || null, postcode: postcode || null, keyStages },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setSetupJustCompleted(true);
      await refreshProfile();
      toast({ title: 'School setup complete!' });
      setCurrentStep(3);
    } catch (err) {
      toast({ title: 'Setup failed', description: (err as Error).message, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---- Render: management mode (school exists) ----------------------------

  if (hasSchool && !setupJustCompleted) {
    const standardClasses = classes.filter((c) => !c.is_custom);
    const customClasses = classes.filter((c) => c.is_custom);

    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 max-w-4xl mx-auto space-y-6">
          <h1 className="text-2xl font-bold">School Management</h1>

          <Tabs defaultValue="details">
            <TabsList className="mb-4">
              <TabsTrigger value="details">School Details</TabsTrigger>
              <TabsTrigger value="classes">Classes</TabsTrigger>
              <TabsTrigger value="teachers">Teachers</TabsTrigger>
            </TabsList>

            {/* ---- School Details tab ---- */}
            <TabsContent value="details">
              <Card>
                <CardHeader>
                  <CardTitle>School Details</CardTitle>
                  <CardDescription>Update your school's basic information.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-1.5">
                    <Label>School Name *</Label>
                    <Input value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="School name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>DfE Number (optional)</Label>
                    <Input value={editDfe} onChange={(e) => setEditDfe(e.target.value)} placeholder="e.g. 123/4567" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Postcode (optional)</Label>
                    <Input value={editPostcode} onChange={(e) => setEditPostcode(e.target.value)} placeholder="e.g. SW1A 1AA" />
                  </div>
                  <Button onClick={handleSaveDetails} disabled={savingDetails || !editName.trim()}>
                    {savingDetails ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Save Details
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ---- Classes tab ---- */}
            <TabsContent value="classes">
              <div className="space-y-4">
                {/* Add class form */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Add Class</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="customClass"
                        checked={newClassCustom}
                        onChange={(e) => setNewClassCustom(e.target.checked)}
                        className="h-4 w-4 rounded border-input"
                      />
                      <Label htmlFor="customClass" className="cursor-pointer">Custom class (no year group — e.g. SEN Set, Speech & Language)</Label>
                    </div>
                    <div className="flex gap-2">
                      {!newClassCustom && yearGroups.length > 0 && (
                        <select
                          value={newClassYearGroupId}
                          onChange={(e) => setNewClassYearGroupId(e.target.value)}
                          className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                        >
                          {yearGroups.map((yg) => (
                            <option key={yg.id} value={yg.id}>{yg.name}</option>
                          ))}
                        </select>
                      )}
                      <Input
                        placeholder="Class name"
                        value={newClassName}
                        onChange={(e) => setNewClassName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAddClass(); }}
                        className="flex-1"
                      />
                      <Button onClick={handleAddClass} disabled={addingClass || !newClassName.trim()}>
                        {addingClass ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Standard classes grouped by year group */}
                {loadingClasses ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
                ) : (
                  <>
                    {yearGroups.map((yg) => {
                      const ygClasses = standardClasses.filter((c) => c.year_group_id === yg.id);
                      if (ygClasses.length === 0) return null;
                      return (
                        <Card key={yg.id}>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">{yg.name}</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-2">
                            {ygClasses.map((cls) => (
                              <div key={cls.id} className="flex items-center gap-2">
                                {editingClassId === cls.id ? (
                                  <>
                                    <Input
                                      value={editingClassName}
                                      onChange={(e) => setEditingClassName(e.target.value)}
                                      className="flex-1 h-8 text-sm"
                                      autoFocus
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleSaveClassName(cls.id);
                                        if (e.key === 'Escape') setEditingClassId(null);
                                      }}
                                    />
                                    <Button size="sm" variant="default" onClick={() => handleSaveClassName(cls.id)}>Save</Button>
                                    <Button size="sm" variant="ghost" onClick={() => setEditingClassId(null)}><X className="h-3 w-3" /></Button>
                                  </>
                                ) : (
                                  <>
                                    <span className="flex-1 text-sm">{cls.name}</span>
                                    <Button size="sm" variant="ghost" onClick={() => { setEditingClassId(cls.id); setEditingClassName(cls.name); }}>Edit</Button>
                                    <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => handleDeleteClass(cls.id)}><Trash2 className="h-3 w-3" /></Button>
                                  </>
                                )}
                              </div>
                            ))}
                          </CardContent>
                        </Card>
                      );
                    })}

                    {/* Custom classes */}
                    {customClasses.length > 0 && (
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium text-muted-foreground">Custom Classes</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          {customClasses.map((cls) => (
                            <div key={cls.id} className="flex items-center gap-2">
                              {editingClassId === cls.id ? (
                                <>
                                  <Input
                                    value={editingClassName}
                                    onChange={(e) => setEditingClassName(e.target.value)}
                                    className="flex-1 h-8 text-sm"
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleSaveClassName(cls.id);
                                      if (e.key === 'Escape') setEditingClassId(null);
                                    }}
                                  />
                                  <Button size="sm" variant="default" onClick={() => handleSaveClassName(cls.id)}>Save</Button>
                                  <Button size="sm" variant="ghost" onClick={() => setEditingClassId(null)}><X className="h-3 w-3" /></Button>
                                </>
                              ) : (
                                <>
                                  <span className="flex-1 text-sm">{cls.name}</span>
                                  <Button size="sm" variant="ghost" onClick={() => { setEditingClassId(cls.id); setEditingClassName(cls.name); }}>Edit</Button>
                                  <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => handleDeleteClass(cls.id)}><Trash2 className="h-3 w-3" /></Button>
                                </>
                              )}
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    )}

                    {classes.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">No classes yet. Add one above.</p>
                    )}
                  </>
                )}
              </div>
            </TabsContent>

            {/* ---- Teachers tab ---- */}
            <TabsContent value="teachers">
              <div className="space-y-4">
                {/* Invite form */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <CardTitle className="text-base">Invite Teacher</CardTitle>
                        <CardDescription>
                          An email invitation will be sent. The teacher will set their own password.
                        </CardDescription>
                      </div>
                      <Button type="button" variant="outline" onClick={() => setTeacherImportOpen(true)}>
                        Import CSV
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-1.5">
                      <Label>Full name (optional)</Label>
                      <Input
                        value={inviteFullName}
                        onChange={(e) => setInviteFullName(e.target.value)}
                        placeholder="Teacher's full name"
                        disabled={inviting}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Email address *</Label>
                      <div className="flex gap-2">
                        <Input
                          type="email"
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          placeholder="teacher@school.org.uk"
                          disabled={inviting}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleInviteTeacher(); }}
                          className="flex-1"
                        />
                        <Button onClick={handleInviteTeacher} disabled={inviting || !inviteEmail.trim()}>
                          {inviting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
                          Invite
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <TeacherImportDialog
                  open={teacherImportOpen}
                  onOpenChange={setTeacherImportOpen}
                  availableClasses={classes.filter((c) => !c.is_custom).map((c) => ({ id: c.id, name: c.name }))}
                  onComplete={loadTeachers}
                />

                {/* Pending invitations */}
                {!loadingTeachers && pendingInvitations.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Pending Invitations</CardTitle>
                      <CardDescription>Admins can revoke or resend an invite email. Pending users do not have access yet.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-1">
                        {pendingInvitations.map((inv) => (
                          <div key={inv.id} className="flex items-center justify-between gap-3 px-3 py-2.5 border rounded-lg">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm truncate">{inv.email}</p>
                              <div className="flex items-center gap-1 text-muted-foreground mt-0.5">
                                <Clock className="h-3 w-3 shrink-0" />
                                <span className="text-xs">Invited — awaiting acceptance</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-xs"
                                disabled={resendingInviteId === inv.id}
                                onClick={() => handleResendInvite(inv.id, inv.email)}
                              >
                                {resendingInviteId === inv.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Resend invite'}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                                disabled={revokingInvitationId === inv.id}
                                onClick={() => handleRevokeInvitation(inv.id, inv.email)}
                              >
                                {revokingInvitationId === inv.id
                                  ? <Loader2 className="h-3 w-3 animate-spin" />
                                  : 'Revoke'}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Active teacher list */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Active School Users</CardTitle>
                    <CardDescription>
                      Admin controls only. SLT can review school-wide data but cannot manage users or school structure.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loadingTeachers ? (
                      <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
                    ) : teachers.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No active teachers yet.</p>
                    ) : (
                      <div className="space-y-1">
                        {teachers.map((t) => {
                          const isExpanded = expandedTeacherId === t.user_id;
                          // Standard (non-custom) classes for assignment controls
                          const assignableClasses = classes.filter((c) => !c.is_custom);

                          let StatusIcon: LucideIcon;
                          let statusLabel: string;
                          let statusClass: string;
                          if (!t.is_confirmed) {
                            StatusIcon = Clock;
                            statusLabel = 'Invited';
                            statusClass = 'text-muted-foreground';
                          } else if (t.class_ids.length === 0) {
                            StatusIcon = AlertTriangle;
                            statusLabel = 'Active · no classes';
                            statusClass = 'text-orange-600 dark:text-orange-400';
                          } else {
                            StatusIcon = CheckCircle2;
                            statusLabel = 'Active · classes assigned';
                            statusClass = 'text-green-600 dark:text-green-400';
                          }

                          return (
                            <div key={t.user_id} className="border rounded-lg overflow-hidden">
                              {/* Main row */}
                              <div className="flex items-center justify-between gap-3 px-3 py-2.5">
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium truncate">{t.full_name ?? '—'}</p>
                                  <p className="text-xs text-muted-foreground truncate">{t.email ?? '—'}</p>
                                  <div className="mt-1">
                                    <span className="inline-flex rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                                      {ROLE_LABELS[t.role] ?? t.role}
                                    </span>
                                  </div>
                                  <div className={cn('flex items-center gap-1 mt-0.5', statusClass)}>
                                    <StatusIcon className="h-3 w-3 shrink-0" />
                                    <span className="text-xs">{statusLabel}</span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                  {t.role !== 'school_admin' && (
                                    <Select
                                      value={t.role === 'slt' ? 'slt' : 'teacher'}
                                      onValueChange={(value) => handleRoleChange(t.user_id, value as 'teacher' | 'slt')}
                                      disabled={roleUpdatingUserId === t.user_id}
                                    >
                                      <SelectTrigger className="h-7 w-[110px] text-xs">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="teacher">Teacher</SelectItem>
                                        <SelectItem value="slt">SLT</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  )}
                                  {t.is_confirmed && t.role === 'teacher' && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 px-2 text-xs"
                                      onClick={() => setExpandedTeacherId(isExpanded ? null : t.user_id)}
                                    >
                                      Assign classes
                                      {isExpanded ? <ChevronUp className="ml-1 h-3 w-3" /> : <ChevronDown className="ml-1 h-3 w-3" />}
                                    </Button>
                                  )}
                                  {t.role !== 'school_admin' && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 px-2 text-xs"
                                      onClick={() => handleSendPasswordReset(t)}
                                      disabled={passwordResetUserId === t.user_id || !t.email}
                                    >
                                      {passwordResetUserId === t.user_id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Send reset'}
                                    </Button>
                                  )}
                                  {t.role !== 'school_admin' && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 px-2 text-xs"
                                      onClick={() => openUpdateEmailDialog(t)}
                                    >
                                      Update email
                                    </Button>
                                  )}
                                  {t.role !== 'school_admin' && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                                      onClick={() => handleRemoveTeacher(t.user_id)}
                                    >
                                      Remove
                                    </Button>
                                  )}
                                </div>
                              </div>

                              {/* Expandable class assignment section */}
                              {isExpanded && (
                                <div className="border-t bg-muted/30 px-3 py-2.5 space-y-2">
                                  <p className="text-xs font-medium text-muted-foreground">Class assignments</p>
                                  {assignableClasses.length === 0 ? (
                                    <p className="text-xs text-muted-foreground">No classes set up yet.</p>
                                  ) : (
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3">
                                      {assignableClasses.map((cls) => {
                                        const key = `${t.user_id}:${cls.id}`;
                                        const checked = t.class_ids.includes(cls.id);
                                        const saving = savingAssignment === key;
                                        return (
                                          <label
                                            key={cls.id}
                                            className="flex items-center gap-1.5 cursor-pointer select-none"
                                          >
                                            {saving ? (
                                              <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground shrink-0" />
                                            ) : (
                                              <input
                                                type="checkbox"
                                                checked={checked}
                                                disabled={saving}
                                                onChange={() => handleToggleClassAssignment(t.user_id, cls.id, !checked)}
                                                className="h-3.5 w-3.5 rounded border-input"
                                              />
                                            )}
                                            <span className="text-xs truncate">{cls.name}</span>
                                          </label>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
        <Dialog open={emailDialogUser !== null} onOpenChange={(open) => { if (!open) setEmailDialogUser(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update user email</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>New email address</Label>
                <Input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="teacher@school.org.uk"
                  disabled={updatingEmailUserId !== null}
                />
              </div>
              <p className="text-sm text-muted-foreground">
                This admin action updates the existing Supabase Auth user directly. Roles, class assignments, and assessment data stay linked because the user ID does not change. No password is visible here.
              </p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setEmailDialogUser(null)} disabled={updatingEmailUserId !== null}>
                Cancel
              </Button>
              <Button onClick={handleUpdateEmail} disabled={!newEmail.trim() || updatingEmailUserId !== null}>
                {updatingEmailUserId !== null ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Update Email
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DashboardLayout>
    );
  }

  // ---- Render: wizard mode (first-time setup) -----------------------------

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-4xl mx-auto space-y-8">
        {/* Progress header */}
        <div className="space-y-4">
          <div>
            <h1 className="text-2xl font-bold">School Setup</h1>
            <p className="text-muted-foreground">Set up your school structure to start using WriteAssess</p>
          </div>
          <div className="flex items-center gap-2">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isComplete = currentStep > step.id;
              return (
                <div key={step.id} className="flex items-center">
                  <div className={cn(
                    'flex items-center gap-2 px-3 py-2 rounded-lg transition-colors',
                    isActive && 'bg-primary text-primary-foreground',
                    isComplete && 'bg-success text-success-foreground',
                    !isActive && !isComplete && 'bg-muted text-muted-foreground'
                  )}>
                    <Icon className="h-4 w-4" />
                    <span className="text-sm font-medium hidden sm:block">{step.title}</span>
                  </div>
                  {index < STEPS.length - 1 && <ChevronRight className="h-4 w-4 text-muted-foreground mx-1" />}
                </div>
              );
            })}
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Step 1 */}
        {currentStep === 1 && (
          <Card className="shadow-soft animate-fade-in">
            <CardHeader>
              <CardTitle>School Details</CardTitle>
              <CardDescription>Enter your school's basic information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="schoolName">School Name *</Label>
                <Input id="schoolName" placeholder="e.g. Oakwood Primary School" value={schoolName} onChange={(e) => setSchoolName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dfeNumber">DfE Number (optional)</Label>
                <Input id="dfeNumber" placeholder="e.g. 123/4567" value={dfeNumber} onChange={(e) => setDfeNumber(e.target.value)} />
                <p className="text-xs text-muted-foreground">Your school's Department for Education reference number</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="postcode">Postcode (optional)</Label>
                <Input id="postcode" placeholder="e.g. SW1A 1AA" value={postcode} onChange={(e) => setPostcode(e.target.value)} />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2 */}
        {currentStep === 2 && (
          <Card className="shadow-soft animate-fade-in">
            <CardHeader>
              <CardTitle>School Structure</CardTitle>
              <CardDescription>Customise your Key Stages, Year Groups, and Classes.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {keyStages.map((ks) => (
                <div key={ks.id} className="border rounded-xl p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-lg">{ks.name}</h3>
                    <Button variant="outline" size="sm" onClick={() => addYearGroup(ks.id)}>
                      <Plus className="h-4 w-4 mr-1" />Add Year Group
                    </Button>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {ks.yearGroups.map((yg) => (
                      <div key={yg.id} className="bg-muted/50 rounded-lg p-3 space-y-3">
                        <div className="flex items-center gap-2">
                          <Input value={yg.name} onChange={(e) => updateYearGroupName(ks.id, yg.id, e.target.value)} className="font-medium" />
                          {ks.yearGroups.length > 1 && (
                            <Button variant="ghost" size="icon" onClick={() => removeYearGroup(ks.id, yg.id)} className="text-destructive hover:text-destructive">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                        <div className="space-y-2">
                          {yg.classes.map((cls) => (
                            <div key={cls.id} className="flex items-center gap-2">
                              <Input value={cls.name} onChange={(e) => updateClassName(ks.id, yg.id, cls.id, e.target.value)} placeholder="Class name" className="text-sm" />
                              {yg.classes.length > 1 && (
                                <Button variant="ghost" size="icon" onClick={() => removeClass(ks.id, yg.id, cls.id)} className="text-destructive hover:text-destructive shrink-0">
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          ))}
                          <Button variant="ghost" size="sm" onClick={() => addClass(ks.id, yg.id)} className="w-full text-xs">
                            <Plus className="h-3 w-3 mr-1" />Add Class
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Step 3 */}
        {currentStep === 3 && (
          <Card className="shadow-soft animate-fade-in text-center">
            <CardHeader>
              <div className="mx-auto h-16 w-16 rounded-full bg-success/10 flex items-center justify-center mb-4">
                <Check className="h-8 w-8 text-success" />
              </div>
              <CardTitle className="text-2xl">Setup Complete!</CardTitle>
              <CardDescription className="text-base">
                Your school has been successfully set up. You can now start adding pupils and uploading writing samples.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col items-center gap-3">
              <Button onClick={() => navigate('/school-billing')} size="lg">
                Choose your plan
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
              <Button variant="ghost" onClick={() => navigate('/dashboard')}>
                Skip for now — explore the dashboard first
              </Button>
              <p className="text-xs text-muted-foreground">
                No card needed. You can choose a plan any time from Settings → Plan &amp; Usage.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        {currentStep < 3 && (
          <div className="flex justify-between">
            <Button variant="outline" onClick={handleBack} disabled={currentStep === 1}>
              <ChevronLeft className="mr-2 h-4 w-4" />Back
            </Button>
            {currentStep === 2 ? (
              <Button onClick={handleWizardSubmit} disabled={isSubmitting}>
                {isSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating...</> : <>Complete Setup<Check className="ml-2 h-4 w-4" /></>}
              </Button>
            ) : (
              <Button onClick={handleNext}>Continue<ChevronRight className="ml-2 h-4 w-4" /></Button>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
